import { h, JSX, Ref } from "preact";

import PreviewBackgroundColor, { isDefaultPreviewBackgroundColor } from "../../../foundation/api/model/colors/PreviewBackgroundColor";
import { Theme } from "../../../foundation/theme";
import classname from "../../../foundation/utils/classname";
import { ImageLightness } from "../../../foundation/utils/image";
import { stopPropagation } from "../../../foundation/utils/legacy";
import IconLink from "../Icons/IconLink";
import IconLinkBroken from "../Icons/IconLinkBroken";
import TextureBackground, { getTextureVariantFromLightness, TextureBackgroundVariant } from "../TextureBackground";
import VariantIndicator from "../VariantIndicator";

import { getComponentCardWidth, getComponentCardImageDimensions, getComponentImageHeight } from "./utils";

import styles from "./ComponentCard.module.css";

type InheritedDivProps = Omit<JSX.HTMLAttributes<HTMLDivElement>, "name">;

interface Props extends InheritedDivProps {
    // Component Data
    name?: string | JSX.Element;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    imageLightness: ImageLightness | null;
    imageHidden?: boolean;
    previewBackgroundColor: PreviewBackgroundColor;
    // Component State
    linked?: boolean;
    linkBroken?: boolean;
    highlighted?: boolean;
    settingAsGroup?: boolean;
    smallSize?: boolean;
    // Refs
    baseRef?: Ref<HTMLDivElement>;
    // Theme
    activeTheme: Theme;
}

function ComponentCard({
    class: cssClass,
    // Component Data
    name,
    imageUrl,
    imageWidth,
    imageHeight,
    imageLightness,
    imageHidden: _img,
    previewBackgroundColor,
    // Component State
    linked,
    linkBroken,
    highlighted,
    settingAsGroup,
    smallSize = false,
    // Refs
    baseRef,
    // Theme
    activeTheme,
    ...nativeDivProps
}: Props) {
    function renderIcon() {
        if (linked) {
            return <IconLink/>;
        }

        if (linkBroken) {
            return <IconLinkBroken/>;
        }

        return null;
    }

    function renderName() {
        if (typeof name === "string") {
            return <p class={classname(styles.name, "body-1 ellipsis")}>{name}</p>;
        }

        return name;
    }

    function renderTextureBackground() {
        if (previewBackgroundColor && !isDefaultPreviewBackgroundColor(previewBackgroundColor)) {
            return (
                <TextureBackground
                    class={styles.texture}
                    variant={TextureBackgroundVariant.Custom}
                    color={previewBackgroundColor}
                    absolute/>
            );
        }

        if (!imageLightness ||
            (activeTheme === Theme.Light && imageLightness !== ImageLightness.Light) ||
            (activeTheme === Theme.Dark && imageLightness !== ImageLightness.Dark)) {
            return null;
        }

        const textureVariant = getTextureVariantFromLightness(imageLightness);

        return <TextureBackground class={styles.texture} variant={textureVariant} absolute/>;
    }

    function renderImage() {
        const componentImageHeight = getComponentImageHeight({ smallSize });

        const imageContainerStyle = {
            height: `${componentImageHeight}px`
        };

        const imageWrapperStyle = getComponentCardImageDimensions(imageWidth, imageHeight, { smallSize });

        return (
            <div class={styles.imageContainer} style={imageContainerStyle}>
                {!!imageUrl && (
                    <div class={styles.imageWrapper} style={imageWrapperStyle}>
                        <img alt="" loading="lazy" class={styles.image} src={imageUrl}/>
                        {renderTextureBackground()}
                    </div>
                )}
            </div>
        );
    }

    function renderSettingAsGroupIndicator() {
        if (!settingAsGroup) {
            return null;
        }

        return (
            <div class={styles.settingAsGroupIndicator}>
                <VariantIndicator label="Create group"/>
            </div>
        );
    }

    const componentCardWidth = getComponentCardWidth(imageWidth, imageHeight, { smallSize });
    const componentCardStyle = {
        width: `${componentCardWidth}px`
    };

    return (
        <div
            class={classname(styles.componentCard, cssClass, {
                [styles.smallSize]: smallSize,
                [styles.highlighted]: highlighted,
                [styles.settingAsGroup]: settingAsGroup
            })} {...nativeDivProps}
            style={componentCardStyle}
            ref={baseRef}>
            {renderSettingAsGroupIndicator()}
            {renderImage()}
            {!!name && (
                <div class={styles.nameContainer} onMouseDown={stopPropagation} onClick={stopPropagation}>
                    {renderIcon()}
                    {renderName()}
                </div>
            )}
            <div class={styles.highlightOverlay}>
                <div class={styles.highlightBody}/>
            </div>
        </div>
    );
}

export default ComponentCard;
export {
    Props as ComponentCardProps
};
